import { Component, OnInit, Input, OnChanges, OnDestroy } from '@angular/core';
import { CommonService } from 'src/app/shared/services/common.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import * as _ from 'underscore';
import { UsersService } from 'src/app/users/users.service';
import { Subscription } from 'rxjs';
import { SiteDashboardService } from '../site-dashboard/site-dashboard.service';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { CommonDataService } from 'src/app/shared/services/common-data.service';
import { cloneDeep } from 'lodash-es';
import { DeviceFactory } from '../site-dashboard/device/device-factory/device-factory';
import { DeviceService } from '../site-dashboard/device/device.service';
import { ALL_PERIODS, PerSiteChartAxis, PerSiteChartsService, PerSiteRow } from 'src/app/shared/services/per-site-charts.service';

@Component({
	selector: 'app-quarterly-usage-data',
	templateUrl: './quarterly-usage-data.component.html'
})
export class QuarterlyUsageDataComponent implements OnInit, OnChanges, OnDestroy {
	@Input() deviceType?: number = DeviceFactory.types.iotah;
	deviceConfig = DeviceFactory.createDevice(this.deviceType).config;

	@Input() data: any = {};
	@Input() devices: any[];
	@Input() parentPage: string = 'device'; // options are: customer, site, device
	@Input() options: {hasTruckTypeFilter, hasStackedColumn, hasSiteFilter, hasQuarterlyMonthlyFilter} = {hasTruckTypeFilter: false, hasStackedColumn: false, hasSiteFilter: false, hasQuarterlyMonthlyFilter: false};
	@Input() tags: any[] = [];
	@Input() sites: any[] = [];
	@Input() deviceTags: any[] = [];
	@Input() siteInfo: any = {};
	@Input() truckTypes: any = {};
	@Input() normalizeData: boolean = false;
	/**
	 * ST-2448: per-site rows from the backend, nested
	 * perSite[deviceType][workKey][year][period]. Untyped because it is the raw payload,
	 * like the `data` input above it.
	 */
	@Input() perSiteData: any = {};

	/** perSiteMeta[deviceType][siteId] = { siteName, installYear }; untyped for the same reason */
	@Input() perSiteMeta: any = {};

	deviceTypesUtilizationTooltips= {
		[DeviceFactory.types.iotah]: 'quarterly_monthly_usage_data_utilization',
		[DeviceFactory.types.charglink]: 'chargLinkDevice.quarterly_monthly_usage_data_utilization',
		[DeviceFactory.types.brm]: 'chargLinkDevice.quarterly_monthly_usage_data_utilization',
		[DeviceFactory.types.battlink]: 'battlinkDevice.quarterly_monthly_usage_data_utilization',
	}

	deviceTypesEnergyUtilizationTooltips= {
		[DeviceFactory.types.iotah]: 'quarterly_monthly_usage_data_utilization_energy',
		[DeviceFactory.types.charglink]: 'chargLinkDevice.quarterly_monthly_usage_data_utilization_energy',
		[DeviceFactory.types.brm]: 'chargLinkDevice.quarterly_monthly_usage_data_utilization_energy',
		[DeviceFactory.types.battlink]: 'battlinkDevice.quarterly_monthly_usage_data_utilization_energy',
	}

	DeviceFactory = DeviceFactory;
	selectedYear = moment().utc().get('year').toString();
	/** empty means All Truck Types, the same way an empty site list means All Sites */
	selectedTruckTypes: string[] = [];
	selectedSites = [];
	selectedTags = [];

	trucksColors = [];

	selectedTagNames = [];

	ddList = {
		quarter: [],
		truckTypes: [],
		periods: [],
	};

	// ST-2448: on Customer Info the charts show one bar per site for a single period,
	// so the period stops being the x-axis and becomes a filter of its own.
	selectedPeriod = ALL_PERIODS;
	perSiteLegend: { text: string, color: string }[] = [];
	// heterogeneous by design: a Google Charts table mixes strings, numbers and {role} objects
	perSiteChartData: any = {
		utilization_score: { columnNames: [], rows: [] },
		energy_utilization_score: { columnNames: [], rows: [] },
	};
	/** ST-2614: each per-site chart's width in % of its container - past 100 it scrolls sideways */
	perSiteChartWidths: Record<string, number> = { utilization_score: 100, energy_utilization_score: 100 };

	/** which row field feeds each chart, per device type */
	perSiteMetrics = {
		iotah: {
			utilization_score: 'truck_utilization_score',
			energy_utilization_score: 'truck_utilization_energy_score',
		},
		chargLink: {
			utilization_score: 'charger_utilization_score',
		},
		battlink: {
			utilization_score: 'battery_utilization_score',
			energy_utilization_score: 'battery_utilization_energy_score',
		},
	};

	perSiteChartKeys = ['utilization_score', 'energy_utilization_score'];

	/** ST-2448 renames these charts on Customer Info only, so the keys are page specific */
	perSiteTitleKeys = {
		iotah: 'customer.charts.truck_utilization_charts',
		chargLink: 'customer.charts.charger_utilization_charts',
		battlink: 'customer.charts.battery_utilization_charts',
	};

	chartConfig = {};
	timeChartConfig = {};

	quarterChartObject = {
		data_total_charge_kwhr_mix: [],
		data_avg_charge_kwhr_mix: [],
		data_total_ahr: [],
		data_avg_ahr: [],
		data_total_kwhr: [],
		data_avg_kwhr: [],
		data_total_lift_time: [],
		data_avg_lift_time: [],
		data_total_travel_time: [],
		data_avg_travel_time: [],
		allColumnNames: [],
		columnNames: [],
		energy_utilization_score: [],
		utilization_score: [],
	};

	quarterChartObjectDefault = {
		data_total_charge_kwhr_mix: [],
		data_avg_charge_kwhr_mix: [],
		data_total_ahr: [],
		data_avg_ahr: [],
		data_total_kwhr: [],
		data_avg_kwhr: [],
		data_total_lift_time: [],
		data_avg_lift_time: [],
		data_total_travel_time: [],
		data_avg_travel_time: [],
		allColumnNames: [],
		columnNames: [],
		energy_utilization_score: [],
		utilization_score: [],
	};

	timeCharts = [
		'data_total_lift_time',
		'data_avg_lift_time',
		'data_total_travel_time',
		'data_avg_travel_time'
	]

	UtilizationSettings = {
		iotah: {
			data_total_utilization_score: 'utilization_score',
			data_total_utilization_energy_score: 'energy_utilization_score'
		},
		chargLink: {
			data_total_charge_utilization_score: 'utilization_score',
			data_total_charge_utilization_energy_score: 'energy_utilization_score'
		},
		battlink: {
			data_total_battery_utilization_score: 'utilization_score',
			data_total_battery_utilization_energy_score: 'energy_utilization_score'
		}
	};

	utilizationSelectedYear = moment().utc().get('year').toString();

	showChart = null;

	/**
	 * Held as a field, not a getter. The print header binds to it, and a getter would
	 * rebuild the array on every change-detection pass - which means on every mouse
	 * move over a chart or a dropdown, several translate lookups at a time.
	 */
	printFilters: { label: string, value: string }[] = [];

	hasData = null;

	types = {
		quarter: 'quarter',
	};

	permissionsSub: Subscription = new Subscription();

	tooltips = '';

	colorsArr = [
		'#3366cc', '#dc3912', '#ff9900', '#109618', '#990099', '#0099c6', '#dd4477', '#66aa00', '#b82e2e', '#316395'
	];

	truckTypesMapper = {};

	monthlyQuarterlyPeriods = [
		{ id: 'quarter', durations: this.translate.instant('g.quarterly') },
		{ id: 'months', durations: this.translate.instant('g.monthly') }
	];
	selectedTruckUsageDataPeriod = 'months';

	constructor(
		private translate: TranslateService,
		public common: CommonService,
		public commonData: CommonDataService,
		private userService: UsersService,
		private siteDashboardService: SiteDashboardService,
		private deviceService: DeviceService,
		private perSiteChartsService: PerSiteChartsService,
	){}

	ngOnInit() {
		this.initData();
		this.deviceConfig = DeviceFactory.createDevice(this.deviceType).config;
		this.initData();
	}

	ngOnChanges(changes) {
		this.deviceConfig = DeviceFactory.createDevice(this.deviceType).config;
		this.quarterChartObject = cloneDeep(this.quarterChartObjectDefault);
		this.initData();
		this.prepareChartHeaders(true);
		this.printFilters = this.buildPrintFilters();
	}

	ngOnDestroy() {
		this.permissionsSub.unsubscribe();
	}

	/** rebuilt only when the filters change; the template binds to the cached field */
	buildPrintFilters(): { label: string, value: string }[] {
		const filters: { label: string, value: string }[] = [
			{ label: 'g.year', value: this.common.getOptionLabel(this.ddList.quarter, this.selectedYear, { valueKey: 'id', labelKey: 'year' }) }
		];

		if (this.options.hasSiteFilter)
			filters.push({ label: 'site.sites', value: this.common.getOptionLabel(this.sites, this.selectedSites, { valueKey: 'id', labelKey: 'name', fallbackKey: 'site.all_sites' }) });

		if (this.parentPage != 'device') {
			filters.push({ label: 'devices.truck_type', value: this.common.getOptionLabel(this.ddList.truckTypes, this.selectedTruckTypes, { valueKey: 'id', labelKey: 'text', fallbackKey: 'site_performance.all_truck_types' }) });
			const tagNames = [].concat(...Object.values(this.selectedTagNames || {}));
			if (tagNames.length)
				filters.push({ label: 'tags.tags', value: tagNames.join(', ') });
		}

		if (this.options.hasQuarterlyMonthlyFilter)
			filters.push({ label: 'g.durations', value: this.common.getOptionLabel(this.monthlyQuarterlyPeriods, this.selectedTruckUsageDataPeriod, { valueKey: 'id', labelKey: 'durations' }) });

		// ST-2448: the per-site bars are drawn for one period, so the print header must name it
		if (this.isPerSiteMode)
			filters.push({ label: 'customer.charts.period', value: this.perSitePeriodDisplay });

		return filters;
	}

	initData() {
		const period = this.deviceConfig.quarterlyUsage.period;
		this.data = this.formatUtilizationData(this.data);

		this.quarterChartObject = { ...this.quarterChartObject, ...this.data };

		if(this.normalizeData)
			this.normalizeQuarterData();

		this.hasData = this.quarterChartObject.data_total_ahr || this.quarterChartObject.data_total_kwhr || this.quarterChartObject.utilization_score || this.quarterChartObject.energy_utilization_score;
		this.showChart = (this.quarterChartObject.data_total_ahr[this.selectedYear]?.length > 0 || this.quarterChartObject.data_total_kwhr[this.selectedYear]?.length > 0 || this.quarterChartObject.utilization_score[this.utilizationSelectedYear]?.length > 0 || this.quarterChartObject.energy_utilization_score[this.utilizationSelectedYear]?.length > 0);
		this.tooltips = this.common.getTooltipKey(`performance_${period}_`+this.parentPage, this.userService.userHasNOCAccess());
		this.tooltips = this.tooltips ? this.deviceService.getTranslationKey(this.tooltips, this.deviceType) : this.tooltips;

		this.chartConfig = {
			legend: 'none',
			bar: {
				groupWidth: 30
			},
			isStacked: this.options.hasStackedColumn,
			tooltip: {isHtml: true},
			vAxis: {}
		};

		this.timeChartConfig = {...this.chartConfig, vAxis: this.calculateTicksForTimeCharts()};

		this.ddList.quarter = Object.keys(this.quarterChartObject.data_total_ahr).sort((a, b) => +b - +a).map((item) => { return { id: item, year: item.replace('_', ' ') } });
		if(this.ddList.quarter[0] && this.selectedYear != this.ddList.quarter[0].id)
			this.selectedYear = this.ddList.quarter[0].id;

		if (this.parentPage == 'customer') {
			this.chartConfig['vAxis'] = { title: this.translate.instant('g.percentage_unit') },
			this.prepareYearsListOptions();
			this.buildPerSiteCharts();
		}
	}

	calculateTicksForTimeCharts() {
		const keys = ['data_total_lift_time', 'data_avg_lift_time', 'data_total_travel_time', 'data_avg_travel_time'];
		const data = this.quarterChartObject;
		let max = 0;

		for(const key of keys) {
			if(data[key][this.selectedYear]) {
				for(const item of data[key][this.selectedYear]) {
					let sum = 0;
					for(let i = 1; i < item.length; i += 2) {
						sum += item[i];
					}

					if(sum > max)
						max = sum;
				}
			}
		}

		let ticks = [];
		let interval = 300;
		let maxInterval = (Math.ceil(max / interval) * interval) * 1.1;

		if(maxInterval)
			interval = maxInterval / 5; // 5 steps only

		if (interval > maxInterval)
			maxInterval = interval * 5;

		for(let i = 0; i <= maxInterval; i += interval) {
			let hours = Math.floor(i / 3600);
			let minutes = Math.floor((i % 3600) / 60);
			let seconds = i % 60;
			let time = (maxInterval > 3600 ? hours + ':' : '') + (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds.toFixed(0);
			ticks.push({v: i, f: time});
		}
		return {ticks, title: `(${maxInterval > 3600 ? 'H:' : ''}M:S)`};
	}

	prepareChartHeaders(firstTime=false) {
		this.quarterChartObject.allColumnNames = [];
		let truckTypesOptions: any = [
			{id: '', text: this.deviceService.translate('site_performance.all_truck_types', this.deviceType)}
		];

		truckTypesOptions.color = '';
		for(const truckType in this.truckTypes) {
			const truckTypeKey = truckType.replace(/[^\w]/gi, '_');
			this.truckTypesMapper[truckTypeKey] = truckType;
			const idx = this.truckTypes[truckType] - 1;
			let color = this.colorsArr[idx%this.colorsArr.length]

			if (this.deviceConfig.quarterlyUsage.hasChartLegend)
				color = this.colorsArr[0];

			truckTypesOptions.push({id: truckTypeKey == '' ? '%' : truckTypeKey, text: truckType, color: color});
			if(this.options.hasTruckTypeFilter || this.parentPage == 'device') {
				this.quarterChartObject.allColumnNames.push(truckType, {role: 'style'});
			}
			for(const chartKey in this.data) {
				for(const year in this.data[chartKey]) {
					for(const q in this.data[chartKey][year]) {
						this.data[chartKey][year][q][this.truckTypes[truckType] + 1] = color;
					}
				}
			}
		}

		this.quarterChartObject.columnNames = ['g', ...this.quarterChartObject.allColumnNames];
		this.trucksColors = truckTypesOptions;
		// ST-2546: the filter is multi-select, so "All Truck Types" is its empty state, not an option
		if(firstTime)
			this.ddList.truckTypes = truckTypesOptions.filter(option => option.id !== '');

		if (this.quarterChartObject.columnNames.length === 1 && this.normalizeData) {
			this.quarterChartObject.columnNames = [
				this.translate.instant('site.quarter'),
				'',
				{ role: 'tooltip' }
			];
		}

		this.matchChartHeadersToDataWidth();
	}

	matchChartHeadersToDataWidth() {
		const columnNames = this.quarterChartObject.columnNames;
		if (!Array.isArray(columnNames))
			return;

		let widestRow = 0;
		for (const key in this.quarterChartObject) {
			if (key === 'columnNames' || key === 'allColumnNames')
				continue;

			const yearlyData = this.quarterChartObject[key];
			if (!yearlyData || typeof yearlyData !== 'object')
				continue;

			for (const year in yearlyData) {
				if (!Array.isArray(yearlyData[year]))
					continue;

				for (const row of yearlyData[year]) {
					if (Array.isArray(row) && row.length > widestRow)
						widestRow = row.length;
				}
			}
		}

		if (!widestRow)
			return;

		// data rows are [label, value, style, value, style, ...]
		while (columnNames.length < widestRow)
			columnNames.push(columnNames.length % 2 ? '' : { role: 'style' });

		// a header wider than the data means those series have no column to sit in
		if (columnNames.length > widestRow)
			this.quarterChartObject.columnNames = columnNames.slice(0, widestRow);
	}

	onSelectedTagsChanges(event, type) {
		this.selectedTags[type] = event;

		// selectedTagNames is keyed by `type`, not a flat array - resetting the whole object here (as
		// before) left selectedTagNames[type] undefined below and threw on every tag selection.
		this.selectedTagNames[type] = [];
		for(let tagId of event) {
			let tag = _.findWhere(this.tags, {id: tagId})
			if(tag)
				this.selectedTagNames[type].push(tag.tag_name)
		}
	}

	buildSiteCharts(type: 'quarter', options: {getYearsList?: boolean} = {}) {
		this.showChart = false;
		const ids = this.getUsageIds();
		type = !this.options.hasQuarterlyMonthlyFilter ? 'quarter' : type;
		this.siteDashboardService.getTruckUsageData(ids, type, {truckYear: '', truckType: this.getSelectedTruckTypes(), truckTag: this.selectedTags, parentPage: this.parentPage}).subscribe((res: any)=>{
			this.handleSiteQuarterDataResponse(res, options.getYearsList);
			this.buildGrid();
		});
	}

	/** the empty truck type is keyed '%', which the backend already reads as "no truck type" */
	getSelectedTruckTypes(): string[] {
		return this.selectedTruckTypes.map(truckTypeKey => this.truckTypesMapper[truckTypeKey] ?? truckTypeKey);
	}

	getUsageIds() {
		let ids;
		switch (this.parentPage) {
			case 'customer':
				ids = (this.selectedSites.length ? this.selectedSites : this.sites.map((item) => item.id));
			break;
			case 'site':
				ids = this.siteInfo.id;
			break;
			case 'device':
				ids = this.devices[0].mac_address;
			break;
		}
		return ids;
	}

	handleSiteQuarterDataResponse(res, getYearsList?) {
		const deviceTypekey = this.deviceConfig.reporting.device;
		// ST-2448: the refresh response carries the per-site rows for the new filter values
		if (res.perSite)
			this.perSiteData = res.perSite;
		this.truckTypes = res.trucksIndexes[deviceTypekey];
		const quarterData = this.formatUtilizationData(res[deviceTypekey]) || {};

		this.quarterChartObject = {
			data_total_charge_kwhr_mix: [],
			data_avg_charge_kwhr_mix: [],
			data_total_ahr: [],
			data_avg_ahr: [],
			data_total_kwhr: [],
			data_avg_kwhr: [],
			data_total_lift_time: [],
			data_avg_lift_time: [],
			data_total_travel_time: [],
			data_avg_travel_time: [],
			energy_utilization_score: [],
			utilization_score: [],
			allColumnNames: this.quarterChartObject.allColumnNames,
			columnNames: this.quarterChartObject.columnNames,
		};

		if(getYearsList && res.years)
			this.ddList.quarter = res.years;

		if(Object.keys(quarterData).length) {
			this.data = res[deviceTypekey];
			this.data = this.formatUtilizationData(this.data);
			this.quarterChartObject = { ...this.quarterChartObject, ...quarterData };
			if(this.normalizeData)
				this.normalizeQuarterData();
			this.prepareChartHeaders();
			this.prepareYearsListOptions();
			quarterData?.allColumnNames?.unshift(this.translate.instant('site.quarter'));
			quarterData?.columnNames?.unshift(this.translate.instant('site.quarter'));
		}

		// outside the guard above on purpose: a filter that returns no period data must still
		// redraw the per-site bars, otherwise stale bars stay on screen
		this.buildPerSiteCharts();
	}

	normalizeQuarterData() {
		const chartKeys = [
			'data_total_ahr',
			'data_avg_ahr',
			'data_total_kwhr',
			'data_avg_kwhr',
			'data_total_lift_time',
			'data_avg_lift_time',
			'data_total_travel_time',
			'data_avg_travel_time',
			'utilization_score',
			'energy_utilization_score'
		];

		for (const key of chartKeys) {
			const yearlyData = this.quarterChartObject[key];
			if (!yearlyData) continue;

			for (const year of Object.keys(yearlyData)) {
				yearlyData[year] = yearlyData[year].map(row =>
					this.normalizeQuarterRow(row)
				);
			}
		}
	}

	normalizeQuarterRow(row: any[]) {
		if (!Array.isArray(row)) return row;

		if (row.length === 1) return [row[0], null, ''];
		if (row.length === 2) return [row[0], row[1], ''];
		if (row.length > 3) return row.slice(0, 3);

		return row;
	}

	rebuildPerformanceCharts(type?) {
		return this.buildSiteCharts(type);
	}

	exportQuarterlyUsageData() {
		// ST-2448: the customer page exports per site now, not per period
		if (this.isPerSiteMode)
			return this.exportPerSiteUsageData();

		let alphabits = this.common.alphabetCharacters;
		const period = this.selectedTruckUsageDataPeriod;
		let reportPeriod = [0, 1, 2, 3];
		if (period === 'months')
			reportPeriod = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

		const perfAnalyticKey = this.deviceConfig.quarterlyUsage.perfAnalyticsKey;
		const headersInfo = this.common.getCsvFileHeadersInfo({customerName: this.siteInfo.name});

		let columnNames = [
			period == 'months' ? this.translate.instant('g.month_option') : this.translate.instant('site.quarter'),
			this.deviceService.translate('perf_analytic.total_inuse_as_from_all_events_raw', this.deviceType),
			this.deviceService.translate('perf_analytic.avg_total_inuse_as_from_all_events_raw', this.deviceType),
			this.deviceService.translate('perf_analytic.total_inuse_ws_from_all_events_raw', this.deviceType),
			this.deviceService.translate('perf_analytic.avg_total_inuse_ws_from_all_events_raw', this.deviceType),
		];

		if(this.deviceConfig.quarterlyUsage.hasTotalLiftTimeChart) {
			columnNames.push(
				this.translate.instant('perf_analytic.total_lift_time_raw'),
				this.translate.instant('perf_analytic.avg_total_lift_time_raw'),
			);
		}

		if (this.deviceConfig.quarterlyUsage.hasTotalTravelTimeChart) {
			columnNames.push(
				this.translate.instant('perf_analytic.total_travel_time_raw'),
				this.translate.instant('perf_analytic.avg_total_travel_time_raw')
			);
		}

		if (this.parentPage == 'customer') {
			// Overwrite columnNames to only utilization scores
			columnNames = [
				period == 'months' ? this.translate.instant('g.month_option') : this.translate.instant('site.quarter'),
				this.translate.instant('utilization.utilization_score')
			];

			if ([DeviceFactory.types.battlink, DeviceFactory.types.lithiumBattlink, DeviceFactory.types.battlinkII, DeviceFactory.types.iotah].includes(this.deviceType))
				columnNames.push(this.translate.instant('utilization.energy_utilization_score'));
		}

		let headerObj = {};
		columnNames.forEach((column, colIdx) => {
			let colId = alphabits[colIdx];
			headerObj[colId] = column;
		});

		let fileName = this.common.getCleanFileName(this.siteInfo.name) +
			'-' + this.selectedYear + '-'
			+ this.deviceService.translate(perfAnalyticKey , this.deviceType);

		const dataObj = [
			...headersInfo,
			headerObj
		];
		const chartData = this.quarterChartObject;

		for (const i of reportPeriod) {
			if (this.parentPage == 'customer') {
				// Only utilization scores data
				const utilizationScore = Object.keys(chartData.utilization_score).length ? chartData.utilization_score[this.selectedYear][i] : [];
				const energyUtilizationScore = Object.keys(chartData?.energy_utilization_score).length ?  chartData?.energy_utilization_score?.[this.selectedYear][i] : [];

				const utilizationScoreSum = this.sumTruckTypeDataPerQuarter(utilizationScore);
				const energyUtilizationScoreSum = this.sumTruckTypeDataPerQuarter(energyUtilizationScore);

				const energyData = [
					(period === "months") ? moment(i + 1, 'MM').format('MMM') : `Q${i + 1}`,
					utilizationScoreSum,
				];

				if ([DeviceFactory.types.battlink, DeviceFactory.types.lithiumBattlink, DeviceFactory.types.battlinkII, DeviceFactory.types.iotah].includes(this.deviceType))
					energyData.push(energyUtilizationScoreSum);

				let vals = {};
				energyData.forEach((col, idx) => {
					let colId = alphabits[idx];
					vals[colId] = col;
				});
				dataObj.push(vals);

			} else {
				// Original full data handling
				const totalAhr = chartData.data_total_ahr[this.selectedYear][i] || [];
				const avgAhr = chartData.data_avg_ahr[this.selectedYear][i] || [];
				const totalKwhr = chartData.data_total_kwhr[this.selectedYear][i] || [];
				const avgKwhr = chartData.data_avg_kwhr[this.selectedYear][i] || [];
				const utilizationScore = chartData.utilization_score[this.selectedYear][i] || [];
				const energyUtilizationScore = chartData.energy_utilization_score[this.selectedYear][i] || [];

				const totalAhrSum = this.sumTruckTypeDataPerQuarter(totalAhr);
				const totalAvgAhrSum = this.sumTruckTypeDataPerQuarter(avgAhr);
				const totalKwhrsSum = this.sumTruckTypeDataPerQuarter(totalKwhr);
				const totalAvgKwhrSum = this.sumTruckTypeDataPerQuarter(avgKwhr);
				const utilizationScoreSum = this.sumTruckTypeDataPerQuarter(utilizationScore);
				const energyUtilizationScoreSum = this.sumTruckTypeDataPerQuarter(energyUtilizationScore);

				const energyData = [
					(period === "months") ? moment(i + 1, 'MM').format('MMM'): `Q${i + 1}`,
					totalAhrSum,
					totalAvgAhrSum,
					totalKwhrsSum,
					totalAvgKwhrSum,
					utilizationScoreSum,
					energyUtilizationScoreSum
				];

				if(this.deviceConfig.quarterlyUsage.hasTotalLiftTimeChart) {

					const totalLiftTime = chartData.data_total_lift_time[this.selectedYear][i] || [];
					const avgLiftTime = chartData.data_avg_lift_time[this.selectedYear][i] || [];

					const totalLiftTimeSum = this.sumTruckTypeDataPerQuarter(totalLiftTime);
					const totalAvgLiftTimeSum = this.sumTruckTypeDataPerQuarter(avgLiftTime);
					energyData.push(totalLiftTimeSum, totalAvgLiftTimeSum);
				}

				if (this.deviceConfig.quarterlyUsage.hasTotalTravelTimeChart) {
					const totalTravelTime = chartData.data_total_travel_time[this.selectedYear][i] || [];
					const avgTravelTime = chartData.data_avg_travel_time[this.selectedYear][i] || [];
					const totalTravelTimeSum = this.sumTruckTypeDataPerQuarter(totalTravelTime);
					const totalAvgTravelTimeSum = this.sumTruckTypeDataPerQuarter(avgTravelTime);

					energyData.push(totalTravelTimeSum, totalAvgTravelTimeSum);
				}

				let vals = {};
				energyData.forEach((col, idx) => {
					let colId = alphabits[idx];
					vals[colId] = col;
				});

				dataObj.push(vals);
			}
		}
		new AngularCsv(dataObj, fileName);
	}

	sumTruckTypeDataPerQuarter(arr) {
		let sum = 0;
		for (let i = 1; i < arr?.length; i += 2) {
			if (typeof arr[i] == 'number') {
				sum += arr[i];
			}
		}
		return sum;
	}

	private buildGrid() {
		// ST-2548: on Customer Info buildPerSiteCharts already set showChart from the per-site bars
		// it drew. The old period data has no key for the per-site All Years option, so reading it
		// here hid the chart after any server refresh made while All Years was selected.
		if (!this.isPerSiteMode) {
			this.showChart	= (
				this.quarterChartObject.data_total_ahr[this.selectedYear].length > 0 ||
				this.quarterChartObject.data_total_kwhr[this.selectedYear].length > 0 ||
				this.quarterChartObject.data_total_lift_time[this.selectedYear].length > 0 ||
				this.quarterChartObject.data_total_travel_time[this.selectedYear].length > 0 ||
				this.quarterChartObject.utilization_score[this.selectedYear].length > 0 ||
				this.quarterChartObject.energy_utilization_score[this.selectedYear].length > 0
			);
		}
		this.hasData = true;
	}

	changeUsagePeriod(value) {
		this.selectedTruckUsageDataPeriod = value;
		this.buildSiteCharts(value);
	}

	/** ST-2448: true while this component draws one bar per site (Customer Info only) */
	get isPerSiteMode(): boolean {
		return this.parentPage == 'customer';
	}

	/** the backend nests the per-site rows under the aggregation work key */
	get perSiteWorkKey(): string {
		return this.selectedTruckUsageDataPeriod == 'quarter' ? 'quarters' : 'months';
	}

	/** trucks stack one segment per truck type; chargers and batteries get one solid bar */
	get isPerSiteStacked(): boolean {
		return this.deviceConfig.reporting.device == 'iotah';
	}

	/** All Years puts years on the x-axis, so the two period filters have nothing to say */
	get isPerSiteAllYears(): boolean {
		return this.perSiteChartsService.isAllYears(this.selectedYear);
	}

	/** true when the x-axis carries periods or years rather than sites */
	get isPerSiteBucketAxis(): boolean {
		return this.perSiteChartsService.isBucketAxis(this.selectedYear, this.selectedPeriod);
	}

	get perSitePeriodDisplay(): string {
		return this.perSiteChartsService.getPeriodDisplay(this.selectedPeriod, this.selectedYear, this.perSiteWorkKey);
	}

	/** every row field this chart plots, used to pick a period that is not empty */
	get perSiteMetricFields(): string[] {
		return Object.values(this.perSiteMetrics[this.deviceConfig.reporting.device] || {});
	}

	get perSiteTitleKey(): string {
		return this.perSiteTitleKeys[this.deviceConfig.reporting.device] || this.deviceConfig.quarterlyUsage.perfAnalyticsKey;
	}

	/** no explicit site pick means every site handed in, which already excludes special sites */
	get perSiteAllowedSiteIds() {
		if (this.selectedSites?.length)
			return this.selectedSites;

		return (this.sites || []).map(site => site.id);
	}

	onPerSiteFilterChange() {
		this.buildPerSiteCharts();
	}

	buildPerSiteCharts() {
		if (!this.isPerSiteMode)
			return;

		const deviceTypeKey = this.deviceConfig.reporting.device;

		this.preparePerSiteYearsList();
		this.ddList.periods = this.perSiteChartsService.getPeriodOptions(
			this.perSiteData, deviceTypeKey, this.perSiteWorkKey, this.selectedYear
		);

		// All Months / All Quarters is the default, and the fallback whenever the selected
		// period is not in the new year's list
		if (!this.ddList.periods.some(period => period.id == this.selectedPeriod))
			this.selectedPeriod = ALL_PERIODS;

		const bucketAxis = this.isPerSiteBucketAxis;

		const buckets = bucketAxis
			? this.perSiteChartsService.getBuckets(
				this.perSiteData, deviceTypeKey, this.perSiteWorkKey, this.selectedYear, this.selectedPeriod
			)
			: [];

		const rows = bucketAxis
			? []
			: this.perSiteChartsService.getRows(
				this.perSiteData, deviceTypeKey, this.perSiteWorkKey, this.selectedYear, this.selectedPeriod
			);

		const shared = {
			seriesIndexes: this.truckTypes,
			colors: this.colorsArr,
			siteIds: this.perSiteAllowedSiteIds,
			periodLabel: this.perSitePeriodDisplay,
			unit: this.translate.instant('g.percentage_unit'),
		};

		this.perSiteLegend = [];
		// ST-2614: both score charts share chartConfig. They are built from the same rows, so they
		// get the same bar positions, axis, width and font size
		let stackedHAxis: PerSiteChartAxis;
		for (const chartKey of this.perSiteChartKeys) {
			const metricField = this.perSiteMetrics[deviceTypeKey]?.[chartKey];
			if (!metricField) {
				this.perSiteChartData[chartKey] = { columnNames: [], rows: [] };
				continue;
			}

			// The truck chart keeps its truck-type segments in every view; only the x-axis
			// changes. Utilization is a percentage, so a bucket spanning several periods
			// averages them rather than adding them into a meaningless total.
			// ST-2614: nor does a score add up across truck types - the bar is the site's own score,
			// shared out between the segments in proportion to their scores
			let built;
			if (bucketAxis && this.isPerSiteStacked)
				built = this.perSiteChartsService.buildStackedBySiteAcrossBuckets(buckets, {
					...shared,
					metricField,
					scaleToSiteTotal: true,
					aggregate: 'avg',
				});
			else if (bucketAxis)
				built = this.perSiteChartsService.buildSitesAcrossBuckets(buckets, { ...shared, metricField, aggregate: 'avg' });
			else if (this.isPerSiteStacked)
				built = this.perSiteChartsService.buildStackedBySiteAndTruckType(rows, { ...shared, metricField, scaleToSiteTotal: true });
			else
				built = this.perSiteChartsService.buildSingleBarBySite(rows, {
					...shared,
					metricField,
					seriesLabel: this.translate.instant(`utilization.${chartKey}`),
				});

			this.perSiteChartData[chartKey] = built;
			this.perSiteChartWidths[chartKey] = this.perSiteChartsService.getChartWidthPercent(built);
			stackedHAxis ||= built.hAxis;
			if (built.legend.length && !this.perSiteLegend.length)
				this.perSiteLegend = built.legend;
		}

		// The truck chart stacks its truck-type segments in every view, because the period
		// and the site share one x-axis label there. A single-bar chart must not stack: its
		// series are the sites, and stacking would pile them into one bar per period.
		this.chartConfig['isStacked'] = this.isPerSiteStacked;
		// wider bars in the period views: the group should read as one block per month
		this.chartConfig['bar'] = { groupWidth: bucketAxis ? '92%' : 30 };
		// ST-2614: the stacked period view puts its bars on a number axis with one label per period;
		// every other view keeps the default category axis
		if (stackedHAxis)
			this.chartConfig['hAxis'] = stackedHAxis;
		else
			delete this.chartConfig['hAxis'];

		delete this.chartConfig['fontSize'];
		Object.assign(this.chartConfig, this.perSiteChartsService.getChartWidthOptions(this.perSiteChartWidths.utilization_score));

		// leave whatever initData decided if the backend has not sent any per-site rows for this type
		if (Object.keys(this.perSiteData?.[deviceTypeKey] || {}).length)
			this.hasData = true;

		this.showChart = this.perSiteChartKeys.some(chartKey => this.perSiteChartData[chartKey].rows.length > 0);
		this.printFilters = this.buildPrintFilters();
	}

	/**
	 * Years that actually have per-site rows. There is no All Years option here: a per-site
	 * bar is drawn for one period, and all years does not name one.
	 */
	preparePerSiteYearsList() {
		const years = this.perSiteChartsService.getYearOptions(
			this.perSiteData, this.deviceConfig.reporting.device, this.perSiteWorkKey,
			{ meta: this.perSiteMeta, siteIds: this.perSiteAllowedSiteIds }
		);

		if (years.length <= 1)
			return;

		// the picker binds to `year` as its label, and All Years sits at the top of the list
		this.ddList.quarter = years.map(item => ({ id: item.id, year: item.label }));
		if (!years.some(item => item.id == this.selectedYear))
			this.selectedYear = years[1].id;
	}

	/** ST-2448: one row per displayed site, taken from the same rows the bars were drawn from */
	exportPerSiteUsageData() {
		const alphabits = this.common.alphabetCharacters;
		const headersInfo = this.common.getCsvFileHeadersInfo({ customerName: this.siteInfo.name });
		const deviceTypeKey = this.deviceConfig.reporting.device;

		// ST-2448: a leading Period column keeps one layout across a single month, All Months
		// and All Years - one selected month simply collapses to one row per site.
		const bucketAxis = this.isPerSiteBucketAxis;
		// the truck chart keeps its truck-type breakdown in every view, so the column stays
		const withTruckType = this.isPerSiteStacked;

		const columnNames = [
			this.translate.instant('customer.charts.period'),
			this.translate.instant('customer.charts.site'),
		];
		if (withTruckType)
			columnNames.push(this.translate.instant('devices.truck_type'));

		const exportedChartKeys = this.perSiteChartKeys.filter(chartKey => this.perSiteMetrics[deviceTypeKey]?.[chartKey]);
		for (const chartKey of exportedChartKeys)
			columnNames.push(this.translate.instant(`utilization.${chartKey}`));

		const headerObj = {};
		columnNames.forEach((column, colIdx) => headerObj[alphabits[colIdx]] = column);

		// the csv rows mix the shared header info objects with this chart's own value rows
		const dataObj: any[] = [...headersInfo, headerObj];

		const buckets = bucketAxis
			? this.perSiteChartsService.getBuckets(
				this.perSiteData, deviceTypeKey, this.perSiteWorkKey, this.selectedYear, this.selectedPeriod
			)
			: [{
				key: this.selectedPeriod,
				label: this.perSitePeriodDisplay,
				rows: this.perSiteChartsService.getRows(
					this.perSiteData, deviceTypeKey, this.perSiteWorkKey, this.selectedYear, this.selectedPeriod
				),
			}];

		const allowed = new Set(this.perSiteAllowedSiteIds.map(id => String(id)));

		for (const bucket of buckets) {
			const exportedRows = bucket.rows.filter(row =>
				allowed.has(String(row.site_id)) && (withTruckType ? row.truck_type != null : row.truck_type == null)
			);

			// A year bucket holds every month in it, so the same site (and truck type) turns up
			// once per month. Collapse them the way the bars do, or the file would repeat rows
			// the chart never drew.
			const grouped = new Map<string, PerSiteRow[]>();
			for (const row of exportedRows) {
				const key = `${row.site_id}|${row.truck_type ?? ''}`;
				if (!grouped.has(key))
					grouped.set(key, []);

				grouped.get(key).push(row);
			}

			for (const groupRows of grouped.values()) {
				// a period and site name followed by numeric scores, so the row is deliberately mixed
				const values: any[] = [bucket.label, groupRows[0].site_name];
				if (withTruckType)
					values.push(groupRows[0].truck_type);

				for (const chartKey of exportedChartKeys) {
					const metricField = this.perSiteMetrics[deviceTypeKey][chartKey];
					const total = groupRows.reduce((sum, row) => sum + (row[metricField] ?? 0), 0);
					// utilization is a percentage, so several periods average rather than add - rounded the
					// way the tooltip shows it, so the file matches the screen (ST-2595)
					values.push(this.perSiteChartsService.roundForDisplay(groupRows.length > 1 ? total / groupRows.length : total));
				}

				const vals = {};
				values.forEach((col, idx) => vals[alphabits[idx]] = col);
				dataObj.push(vals);
			}
		}

		const period = this.perSitePeriodDisplay.replace(/\s+/g, '-');
		const fileName = this.common.getCleanFileName(this.siteInfo.name)
			+ '-' + period
			+ '-' + this.common.getCleanFileName(this.translate.instant(this.perSiteTitleKey));

		new AngularCsv(dataObj, fileName);
	}

	prepareYearsListOptions() {
		const keys = Object.keys(this.quarterChartObject.utilization_score);

		const allKey = keys.find(k => k === 'all');
		const yearKeys = keys.filter(k => k !== 'all');
		yearKeys.sort((a, b) => +b - +a);

		this.ddList.quarter = [
			...(allKey ? [{ id: allKey, year: this.translate.instant('durations.all_years') }] : []), // all years first
			...yearKeys.map(item => ({ id: item, year: item.replace('_', ' ') }))
		];
	}

	formatUtilizationData(data) {
		const deviceScoreType = this.UtilizationSettings?.[this.deviceConfig.reporting.device];

		if (!deviceScoreType) return;

		Object.keys(deviceScoreType).forEach(key => {
			if (data[key] !== undefined) {
				data[deviceScoreType[key]] = data[key];
				delete data[key];
			}
		});

		return data;
	}
}
